import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { Logo } from '../components/icons/Logo';
import toast from 'react-hot-toast';
import { Input, Button } from '../components/ui';
import { motion, useReducedMotion, type Variants } from 'motion/react';

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const prefersReducedMotion = useReducedMotion();

    // Page-load entrance variants: base=300ms, slow=500ms — numeric equivalents of
    // --snapfy-duration-base and --snapfy-duration-slow; easing mirrors --snapfy-ease-standard.
    const headingVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: -12 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
    };
    const cardVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.15 } },
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await AuthService.forgotPassword(email);
            setIsSubmitted(true);
            toast.success('Link de recuperação enviado com sucesso!');
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || 'Falha ao enviar link. Verifique o email informado.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-snapfy-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <motion.div
                className="sm:mx-auto sm:w-full sm:max-w-md"
                variants={headingVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="flex justify-center">
                    <Logo className="h-12 w-auto" />
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-snapfy-text-primary">
                    Recuperar Senha
                </h2>
                <p className="mt-2 text-center text-sm text-snapfy-text-secondary">
                    Ou{' '}
                    <Link to="/login" className="font-medium text-snapfy-primary hover:text-snapfy-accent-soft">
                        voltar para o login
                    </Link>
                </p>
            </motion.div>

            <motion.div
                className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="card bg-snapfy-surface py-8 px-4 shadow-card rounded-card sm:px-10 border border-snapfy-border">
                    {!isSubmitted ? (
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                label="Endereço de Email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="seu@email.com"
                            />

                            <div>
                                <Button
                                    type="submit"
                                    variant="solid"
                                    className="w-full"
                                    isLoading={isLoading}
                                    disabled={isLoading}
                                >
                                    Enviar Link de Recuperação
                                </Button>
                            </div>
                        </form>
                    ) : (
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-900/30 mb-4">
                                <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-snapfy-text-primary">Email Enviado!</h3>
                            <p className="mt-2 text-sm text-snapfy-text-secondary">
                                Verifique sua caixa de entrada (e spam) para encontrar o link de redefinição de senha.
                            </p>
                            <div className="mt-6">
                                <Link
                                    to="/login"
                                    className="w-full flex justify-center py-2.5 px-4 border border-snapfy-border rounded-button shadow-card bg-snapfy-inner text-xs font-bold uppercase tracking-widest text-snapfy-text-secondary hover:border-snapfy-primary hover:text-snapfy-text-primary focus-visible:outline-none focus-visible:ring-snapfy-focus transition-all duration-200"
                                >
                                    Voltar para Login
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default ForgotPasswordPage;
